import { takeLatest, all, put, call, select, fork, delay } from 'redux-saga/effects';
import get from 'lodash/get';
import TokenProvider, {
    setValue,
} from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';

import { getRewardsSyf } from 'yoda-interfaces/lib/Rewards/RewardsApi';
import FactorySaga from './FactorySaga';
import {
    APPLY_SYF_START,
    APPLY_SYF_START_SUCCESS,
    APPLY_SYF_START_ERROR,
} from '../actionTypes/ApplySyfActionTypes';
import AnalyticsAction from '../actions/AnalyticsAction';

import { serviceErrorToAnalyticsFormat } from '../analytics/DigitalAnalyticsHelper';

import { getApplySyfPreferences } from '../selectors/applySyfSelector';

import { ACCOUNT_ID, DP_USER_STATE, DP_APPLY_SYF } from '../common/Constants';

function* redirectAferDelay(url, toOpenInNewTab, fromVanityPage) {
    try {
        yield delay(10);
        // console.log('Redirected', toOpenInNewTab, url);
        fromVanityPage
            ? window.location.replace(url)
            : window.open(url, (toOpenInNewTab && '_blank') || '_self', '');
    } catch (err) {
        console.error('some error occured:', err);
    }
}

function* storeApplySyfInfo(storeInfo = {}) {
    // clears the previous value and reset
    setValue(DP_APPLY_SYF, storeInfo);
    yield;
}

/**
 * This is Saga - forward flow for apply credit card action
 * @param {object} action
 */
export function* startSyfApplicationSaga(action) {
    try {
        const response = yield call(FactorySaga, getRewardsSyf, action); // convert to guest

        if (response.isSuccess) {
            const sdp = get(response, 'response.data.response', '');
            const isContinuedAsGuest = get(action, 'payload.isContinuedAsGuest', false);

            const applySyfPreferences = yield select(getApplySyfPreferences);

            // to store return url from the point it was started
            let referrer = get(action, 'payload.referrer');
            // payload higher precedance

            const fromVanityPage = get(action, 'payload.fromVanityPage', false);

            if (!referrer) {
                // 1. fetch referrer from client

                const clientReferrer =
                    !__SERVER__ &&
                    (fromVanityPage
                        ? document.referrer // hit from vanity page
                        : window.location.href); // hit from other page directly
                // 2. fetch default from config
                referrer = clientReferrer || get(applySyfPreferences, 'defaultReferrer', '/');
            }

            const toOpenInNewTab =
                (get(action, 'payload.toOpenInNewTab') === undefined &&
                    applySyfPreferences.toOpenInNewTab) ||
                get(action, 'payload.toOpenInNewTab');

            const data = {
                sdp,
                accountId: TokenProvider.get(ACCOUNT_ID) || '',
                userState: TokenProvider.get(DP_USER_STATE) || '0',
                isContinuedAsGuest,
                referrer,
                toOpenInNewTab,
                fromVanityPage,
            };

            // to make it configurable
            // and action based too
            const defaultUrl = get(
                applySyfPreferences,
                'defaultUrl',
                'https://apply.syf.com/eapply/eapply.action?clientCode=JCPCON'
            );
            const url = `${get(action, 'payload.dataHref', defaultUrl)}&SDP=${sdp}`;

            const applySyfLocalStore = {
                forwardFlow: {
                    ...data,
                },
            };
            yield call(storeApplySyfInfo, applySyfLocalStore); // changed from fork to call

            yield all([
                fork(
                    redirectAferDelay,
                    url,
                    toOpenInNewTab,
                    get(action, 'payload.fromVanityPage', false)
                ),
                put({
                    type: APPLY_SYF_START_SUCCESS,
                    payload: { data },
                }),
            ]);
            // TODO: to write invalidate sdp through timer
        } else {
            const error = get(response, 'response.data', { errorCode: '' });
            const errorDetails = serviceErrorToAnalyticsFormat(error);
            yield all([
                put({
                    type: APPLY_SYF_START_ERROR,
                    payload: { error },
                }),
                put(AnalyticsAction.triggerFormError(errorDetails)),
            ]);
        }
    } catch (err) {
        const error = {
            errorMessage: err.message,
            errorCode: 'EXCEPTION_IN_APPLY_SYF_START_SAGA',
        };
        const errorPayload = {
            error,
        };

        const errorDetails = serviceErrorToAnalyticsFormat(error);

        yield all([
            put({
                type: APPLY_SYF_START_ERROR,
                payload: errorPayload,
            }),
            put(AnalyticsAction.triggerFormError(errorDetails)),
        ]);
    }
}

const watchApplySyfSaga = function* watchApplySyfSaga() {
    yield takeLatest(APPLY_SYF_START, startSyfApplicationSaga);
};
watchApplySyfSaga.sagaName = 'watchApplySyfSaga';

export default watchApplySyfSaga;
